(() => {
  if (customElements.get('print-configurator')) {
    return;
  }

  function parseDimensions(sizeValue) {
    if (!sizeValue) {
      return null;
    }

    const match = sizeValue.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);

    if (!match) {
      return null;
    }

    return {
      width: parseFloat(match[1]),
      height: parseFloat(match[2]),
    };
  }

  function computeDisplayDimensions(sizeValue, productImage) {
    const base = parseDimensions(sizeValue);

    if (!base || !productImage) {
      return base;
    }

    const longSide = Math.max(base.width, base.height);
    const imageWidth = productImage.naturalWidth;
    const imageHeight = productImage.naturalHeight;

    if (imageWidth === imageHeight) {
      return { width: longSide, height: longSide };
    }

    if (imageWidth < imageHeight) {
      const width = Math.round((longSide * imageWidth / imageHeight) * 10) / 10;

      return { width, height: longSide };
    }

    const height = Math.round((longSide * imageHeight / imageWidth) * 10) / 10;

    return { width: longSide, height };
  }

  function formatDisplaySize(display) {
    return `${Math.round(display.width)}X${Math.round(display.height)} in.`;
  }

  function formatExactSize(display) {
    return `${display.width}X${display.height}`;
  }

  class PrintConfigurator extends HTMLElement {
    static OPTIONS_SECTION_ID = 'conf-product-options';

    constructor() {
      super();

      this.onMaterialChange = this.onMaterialChange.bind(this);
      this.onOptionChange = this.onOptionChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
    }

    connectedCallback() {
      this.init();

      this.attachEvents();
    }

    init() {
      this.mainProductHandle = this.dataset.mainProductHandle;
      this.artworkPrintValueId = this.dataset.artworkPrintValueId;
      this.formatMoney = this.dataset.moneyFormat;
      this.materialSelect = this.querySelector('[data-config-material]');
      this.optionsContainer = this.querySelector('[data-config-options-container]');
      this.priceElement = this.querySelector('[data-config-price]');
      this.submitButton = this.querySelector('[data-submit]');
      this.cartIcon = this.submitButton?.querySelector('.cart-icon');
      this.loadingIcon = this.submitButton?.querySelector('.loading-icon');
      this.cartIcon?.classList.add('loaded');
      this.configVariantId = null;
      this.printVariant = null;
      this.productImage = null;
      this.printConfiguratorDataElement = document.getElementById('print-configurator-data');
      this.configuratorData = this.printConfiguratorDataElement ? JSON.parse(this.printConfiguratorDataElement.textContent) : {};

      const materialSizesEl = document.getElementById('print-configurator-material-sizes');
      this.materialSizesData = materialSizesEl ? JSON.parse(materialSizesEl.textContent) : null;

      const productImageUrl = this.configuratorData?.productImage;

      if (productImageUrl) {
        const productImageElement = new Image();

        productImageElement.addEventListener('load', () => {
          this.productImage = productImageElement;
        });

        productImageElement.src = productImageUrl;
      }

      if (this.mainProductHandle && this.artworkPrintValueId) {
        this.getPrintVariant();
      }
    }

    attachEvents() {
      this.materialSelect.addEventListener('change', this.onMaterialChange);
      this.optionsContainer.addEventListener('change', this.onOptionChange);
      this.submitButton.addEventListener('click', this.onSubmit);
    }

    disconnectedCallback() {
      this.materialSelect.removeEventListener('change', this.onMaterialChange);
      this.optionsContainer.removeEventListener('change', this.onOptionChange);
      this.submitButton.removeEventListener('click', this.onSubmit);
    }

    updateSizeOptions() {
      const sizeSelect = this.optionsContainer.querySelector('[data-size-select]');

      if (!sizeSelect || !this.productImage) {
        return;
      }

      for (const option of sizeSelect.options) {
        const baseSize = option.dataset.valueName;
        const display = computeDisplayDimensions(baseSize, this.productImage);

        if (display) {
          option.textContent = formatDisplaySize(display);
          option.dataset.valueName = formatExactSize(display);
        }
      }
    }

    async getPrintVariant() {
      const url = `/products/${this.mainProductHandle}?section_id=${PrintConfigurator.OPTIONS_SECTION_ID}&option_values=${this.artworkPrintValueId}`;
      const { variant } = await this.fetchSection(url);

      if (variant) {
        this.printVariant = variant;

        await this.onOptionChange();

        this.applyInitialMaterialFilter();
      }
    }

    async onMaterialChange() {
      this.classList.add('is-loading');

      const sizeSelect = this.optionsContainer.querySelector('[data-size-select]');
      const savedSizeBaseSize = sizeSelect?.options[sizeSelect.selectedIndex]?.dataset.baseSize ?? null;

      const handle = this.materialSelect.value;
      const url = `/products/${handle}?section_id=${PrintConfigurator.OPTIONS_SECTION_ID}`;
      const { optionsElement, configuratorData } = await this.fetchSection(url);

      if (configuratorData) {
        this.configuratorData = {
          ...configuratorData,
          productImage: this.configuratorData?.productImage ?? configuratorData.productImage,
          interiorImage: this.configuratorData?.interiorImage ?? configuratorData.interiorImage,
        };
      }

      if (optionsElement) {
        this.replaceOptions(optionsElement);

        this.updateSizeOptions();

        if (savedSizeBaseSize) {
          const newSizeSelect = this.optionsContainer.querySelector('[data-size-select]');

          if (newSizeSelect) {
            for (const option of newSizeSelect.options) {
              if (option.dataset.baseSize === savedSizeBaseSize) {
                newSizeSelect.value = option.value;
                break;
              }
            }
          }
        }
      }

      await this.onOptionChange();

      this.classList.remove('is-loading');
    }

    async onOptionChange(event) {
      if (event?.target?.hasAttribute('data-size-select')) {
        const selectedOption = event.target.options[event.target.selectedIndex];
        const baseSize = selectedOption?.dataset.baseSize ?? '';
        const needsMaterialSwitch = this.filterMaterialsBySize(baseSize);

        if (needsMaterialSwitch) {
          await this.onMaterialChange();
          return;
        }
      }

      this.classList.add('is-loading');

      const handle = this.materialSelect.value;
      const optionValueIds = this.getSelectedOptionValueIds();

      if (!optionValueIds.length) {
        this.classList.remove('is-loading');

        return;
      }

      const url = `/products/${handle}?section_id=${PrintConfigurator.OPTIONS_SECTION_ID}&option_values=${optionValueIds.join(',')}`;
      let { variant, optionsElement } = await this.fetchSection(url);

      for (let index = optionValueIds.length - 1; index > 0 && !variant; index--) {
        const partialIds = optionValueIds.slice(0, index);
        const partialUrl = `/products/${handle}?section_id=${PrintConfigurator.OPTIONS_SECTION_ID}&option_values=${partialIds.join(',')}`;
        const result = await this.fetchSection(partialUrl);

        variant = result.variant;
        optionsElement = result.optionsElement;
      }

      const previousFrameColor = this.optionsContainer.querySelector('[data-frame-color-select]')?.value ?? '';

      if (optionsElement) {
        this.replaceOptions(optionsElement);

        this.updateSizeOptions();
      }

      this.updateFrameColorSelect(previousFrameColor);

      this.configVariantId = variant?.id ?? null;

      const configPrice = variant?.price ?? 0;
      const printPrice = this.printVariant?.price ?? 0;

      this.updatePrice(printPrice + configPrice);

      this.dispatchPreviewChange();

      this.classList.remove('is-loading');
    }

    dispatchPreviewChange() {
      const optionValues = {};
      const selectOptions = this.optionsContainer.querySelectorAll('select[data-config-option]');
      const inputOptions = this.optionsContainer.querySelectorAll('input[data-option-name]');
      const frameColorSelect = this.optionsContainer.querySelector('[data-frame-color-select]');

      for (const element of [...selectOptions, ...inputOptions]) {
        const label = element.dataset.optionName ?? '';
        const optionName = element.tagName === 'SELECT'
          ? (element.options[element.selectedIndex]?.dataset.valueName ?? '')
          : (element.dataset.optionValueName ?? '');

        if (label) {
          optionValues[label] = optionName;
        }
      }

      if (frameColorSelect && !frameColorSelect.closest('[data-frame-color-wrapper]')?.classList.contains('u-hide')) {
        optionValues['_frameColor'] = frameColorSelect.value;
      }

      document.dispatchEvent(new CustomEvent('print-configurator:change', {
        detail: { optionValues },
      }));
    }

    getSelectedOptionValueIds() {
      return Array.from(this.optionsContainer.querySelectorAll('[data-config-option]'))
        .map((option) => option.value)
        .filter(Boolean);
    }

    replaceOptions(newOptionsElement) {
      this.optionsContainer.replaceChildren(...newOptionsElement.childNodes);
    }

    updateFrameColorSelect(previousColor = '') {
      const frameColorWrapper = this.optionsContainer.querySelector('[data-frame-color-wrapper]');
      const frameColorSelect = this.optionsContainer.querySelector('[data-frame-color-select]');

      if (!frameColorWrapper || !frameColorSelect) {
        return;
      }

      const frameOption = this.configuratorData?.frameOption;

      if (!frameOption) {
        return;
      }

      const frameSelect = this.optionsContainer.querySelector('select[data-frame-select]');
      const frameValue = frameSelect?.options[frameSelect.selectedIndex]?.dataset.valueName?.toLowerCase().trim() ?? '';

      const isNoFrame = !frameValue || frameValue === 'no';
      const frameStyle = isNoFrame ? null : (frameOption.styles?.[frameValue] ?? null);
      const colors = frameStyle?.colors ?? {};
      const colorNames = Object.keys(colors);

      if (isNoFrame || colorNames.length === 0) {
        frameColorWrapper.classList.add('u-hide');

        return;
      }

      frameColorSelect.replaceChildren(...colorNames.map((name) => {
        const option = document.createElement('option');

        option.value = name;
        option.textContent = name;

        return option;
      }));

      if (previousColor && colorNames.includes(previousColor)) {
        frameColorSelect.value = previousColor;
      }

      frameColorWrapper.classList.remove('u-hide');
    }

    updatePrice(price) {
      const formatPrice = this.formatPrice(price);

      this.priceElement.textContent = formatPrice;
    }

    formatPrice(price) {
      return this.formatMoney.replace('{{amount}}', (price / 100).toFixed(2));
    }

    async onSubmit() {
      if (!this.printVariant || !this.configVariantId) {
        return;
      }

      this.setLoading(true);

      const bundleTimestamp = Date.now().toString();
      const sizeSelect = this.optionsContainer.querySelector('[data-size-select]');
      const sizeProperties = {};

      if (sizeSelect) {
        const selectedOption = sizeSelect.options[sizeSelect.selectedIndex];
        const baseSize = selectedOption?.dataset.baseSize ?? '';
        const displaySize = selectedOption?.dataset.valueName ?? '';
        const displayDimensions = parseDimensions(displaySize);
        const formattedSize = displayDimensions ? formatDisplaySize(displayDimensions) : displaySize;

        if (baseSize) {
          sizeProperties['_Base size'] = baseSize;
        }

        if (formattedSize) {
          sizeProperties['Print size'] = formattedSize;
        }
      }

      const frameOption = this.configuratorData?.frameOption;
      const frameProperties = {};

      if (frameOption) {
        const frameSelect = this.optionsContainer.querySelector('select[data-frame-select]');
        const frameValue = frameSelect?.options[frameSelect.selectedIndex]?.dataset.valueName ?? '';

        if (frameValue && frameValue.toLowerCase() !== 'no') {
          frameProperties['Frame type'] = frameValue;
        }

        const frameColorSelect = this.optionsContainer.querySelector('[data-frame-color-select]');
        const isHiddenColorSelect = frameColorSelect?.closest('[data-frame-color-wrapper]')?.classList.contains('u-hide');

        if (frameColorSelect && !isHiddenColorSelect) {
          frameProperties['Frame color'] = frameColorSelect.value;
        }
      }

      const result = await this.addToCart([
        {
          id: this.printVariant.id,
          quantity: 1,
          properties: { _mainBundle: bundleTimestamp, ...sizeProperties, ...frameProperties },
        },
        {
          id: this.configVariantId,
          quantity: 1,
          properties: { _childBundle: bundleTimestamp },
        },
      ]);

      this.setLoading(false);

      if (!result.status) {
        this.openCart();
      }
    }

    applyInitialMaterialFilter() {
      const sizeSelect = this.optionsContainer.querySelector('[data-size-select]');
      if (!sizeSelect) return;
      const baseSize = sizeSelect.options[sizeSelect.selectedIndex]?.dataset.baseSize ?? '';
      const needsSwitch = this.filterMaterialsBySize(baseSize);
      if (needsSwitch) {
        this.onMaterialChange();
      }
    }

    filterMaterialsBySize(baseSize) {
      if (!this.materialSizesData || !baseSize) return false;

      for (const option of this.materialSelect.options) {
        const availableSizes = this.materialSizesData[option.value] ?? [];
        option.disabled = !availableSizes.includes(baseSize);
      }

      const currentOption = this.materialSelect.options[this.materialSelect.selectedIndex];
      if (currentOption?.disabled) {
        for (const option of this.materialSelect.options) {
          if (!option.disabled) {
            this.materialSelect.value = option.value;
            return true;
          }
        }
      }

      return false;
    }

    setLoading(loading) {
      this.submitButton.disabled = loading;
      this.cartIcon.style.display = loading ? 'none' : 'inline-flex';
      this.loadingIcon.style.display = loading ? 'block' : 'none';
    }

    async addToCart(items) {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      return response.json();
    }

    openCart() {
      const form = this.querySelector('[data-cart-trigger]');

      if (!form) {
        return;
      }

      form.querySelector('[type="submit"]').click();
    }

    async fetchSection(url) {
      const response = await fetch(url);
      const text = await response.text();
      const document = new DOMParser().parseFromString(text, 'text/html');
      const variant = JSON.parse(document.getElementById('resolved-variant').textContent);
      const configuratorDataEl = document.getElementById('print-configurator-data');
      const configuratorData = configuratorDataEl ? JSON.parse(configuratorDataEl.textContent) : null;

      return {
        variant,
        optionsElement: document.querySelector('[data-config-product-id]'),
        configuratorData,
      };
    }
  }

  customElements.define('print-configurator', PrintConfigurator);
})();
